import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../../state/context/AuthContext';
import api from '../../api/client/axiosInstance';
import imageService from '../../api/services/imageService';
import toast from 'react-hot-toast';
import {
  FaCloudUploadAlt,
  FaFileImage,
  FaTimes,
  FaCheck,
  FaExclamationTriangle,
  FaPlus,
  FaFolder,
  FaClock,
  FaRedoAlt,
  FaVideo,
} from 'react-icons/fa';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useQuery } from '@tanstack/react-query';
import { getVideoDuration, trimVideoTo30Seconds, isVideoFile, VIDEO_TRIM_THRESHOLD_SECONDS } from '../../utils/videoTrim';
import { fieldsFromUploadResponse, startVideoProcessingPoll } from '../../utils/mediaUploadQueue';

// ---------------------------------------------------------------------------
// Persistent queue types and IndexedDB (survives refresh/navigation)
// ---------------------------------------------------------------------------

const UPLOAD_DB_NAME = 'FileVaultUploadQueue';
const UPLOAD_DB_VERSION = 1;
const UPLOAD_STORE_NAME = 'queue';

export type QueueItemStatus = 'waiting' | 'processing' | 'uploading' | 'paused' | 'completed' | 'failed';

export interface QueueItemMeta {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  fileKey: string; // name+size+lastModified for duplicate detection
  status: QueueItemStatus;
  progress: number;
  retries: number;
  response?: unknown;
  error?: string;
  successMessage?: string;
  uploadDestination?: 'my-account' | 'family-account';
  targetFamilyMember?: {
    otherUserId: number;
    otherUserFirstName: string;
    otherUserLastName?: string;
    relationshipType?: string;
  };
  imageId?: number | string;
  videoId?: number;
  streamUrl?: string;
  statusPollUrl?: string;
  mediaType?: 'IMAGE' | 'VIDEO';
  videoStatus?: string;
  /** When set, this upload will be added to this album once it completes */
  targetAlbumId?: number;
  createdAt: number;
}

export interface QueueItemStored extends QueueItemMeta {
  blob: Blob;
}

export interface QueueItem extends QueueItemMeta {
  file: File; // in-memory only; when restored from IDB we build from blob
}

// IndexedDB helpers
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(UPLOAD_DB_NAME, UPLOAD_DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(UPLOAD_STORE_NAME)) {
        db.createObjectStore(UPLOAD_STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

async function getAllStored(): Promise<QueueItemStored[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(UPLOAD_STORE_NAME, 'readonly');
    const store = tx.objectStore(UPLOAD_STORE_NAME);
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

async function putStored(item: QueueItemStored): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(UPLOAD_STORE_NAME, 'readwrite');
    tx.objectStore(UPLOAD_STORE_NAME).put(item);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
}

async function deleteStored(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(UPLOAD_STORE_NAME, 'readwrite');
    tx.objectStore(UPLOAD_STORE_NAME).delete(id);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
}

function fileToKey(file: File): string {
  return `${file.name}-${file.size}-${(file as File & { lastModified?: number }).lastModified ?? Date.now()}`;
}

// ---------------------------------------------------------------------------
// Upload Manager (singleton – runs outside React, survives unmount)
// ---------------------------------------------------------------------------

const MAX_CONCURRENT = 3;
const MAX_RETRIES = 3;
const UPLOAD_TIMEOUT_MS = 0; // no timeout for large files
const MAX_UPLOAD_QUEUE = 100;

type Listener = () => void;

class UploadManager {
  private items: QueueItem[] = [];
  private isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
  private listeners = new Set<Listener>();
  private activeUploads = new Map<string, AbortController>();
  private processing = false;

  getState(): { items: QueueItem[]; isOnline: boolean } {
    return { items: [...this.items], isOnline: this.isOnline };
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    this.listeners.forEach((l) => l());
  }

  setOnline(online: boolean): void {
    if (this.isOnline === online) return;
    this.isOnline = online;
    if (!online) {
      this.activeUploads.forEach((ac) => ac.abort());
      this.activeUploads.clear();
      this.items = this.items.map((it) =>
        it.status === 'uploading' ? { ...it, status: 'paused' as const } : it
      );
      this.persistAll();
    }
    this.notify();
    if (online) this.processQueue();
  }

  async loadFromPersisted(): Promise<void> {
    try {
      const stored = await getAllStored();
      const items: QueueItem[] = stored.map((s) => ({
        ...s,
        file: new File([s.blob], s.fileName, { type: s.fileType }),
      }));
      this.items = items;
      this.notify();
      if (this.isOnline) this.processQueue();
    } catch (e) {
      console.error('[UploadManager] loadFromPersisted failed', e);
    }
  }

  private async persistItem(item: QueueItem): Promise<void> {
    try {
      const blob: Blob = item.file;
      await putStored({
        id: item.id,
        fileName: item.fileName,
        fileSize: item.fileSize,
        fileType: item.fileType,
        fileKey: item.fileKey,
        status: item.status,
        progress: item.progress,
        retries: item.retries,
        response: item.response,
        error: item.error,
        successMessage: item.successMessage,
        uploadDestination: item.uploadDestination,
        targetFamilyMember: item.targetFamilyMember,
        imageId: item.imageId,
        videoId: item.videoId,
        streamUrl: item.streamUrl,
        statusPollUrl: item.statusPollUrl,
        mediaType: item.mediaType,
        videoStatus: item.videoStatus,
        targetAlbumId: item.targetAlbumId,
        createdAt: item.createdAt,
        blob,
      });
    } catch (e) {
      console.error('[UploadManager] persistItem failed', e);
    }
  }

  private async persistAll(): Promise<void> {
    for (const item of this.items) {
      await this.persistItem(item);
    }
  }

  async addFiles(files: File[], options?: { uploadDestination?: 'my-account' | 'family-account'; targetFamilyMember?: QueueItem['targetFamilyMember']; targetAlbumId?: number }): Promise<{ added: number; skipped: number; skippedDueToLimit: number }> {
    const existingKeys = new Set(this.items.map((i) => i.fileKey));
    let added = 0;
    let skipped = 0;
    let skippedDueToLimit = 0;
    for (const file of files) {
      if (this.items.length >= MAX_UPLOAD_QUEUE) {
        skippedDueToLimit++;
        continue;
      }
      const fileKey = fileToKey(file);
      if (existingKeys.has(fileKey)) {
        skipped++;
        continue;
      }
      existingKeys.add(fileKey);
      const id = `upload-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      const item: QueueItem = {
        id,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type || 'application/octet-stream',
        fileKey,
        status: 'waiting',
        progress: 0,
        retries: 0,
        uploadDestination: options?.uploadDestination ?? 'my-account',
        targetFamilyMember: options?.targetFamilyMember,
        targetAlbumId: options?.targetAlbumId,
        createdAt: Date.now(),
        file,
      };
      this.items.push(item);
      await this.persistItem(item);
      added++;
    }
    this.notify();
    if (this.isOnline) this.processQueue();
    return { added, skipped, skippedDueToLimit };
  }

  async remove(id: string): Promise<void> {
    const ac = this.activeUploads.get(id);
    if (ac) {
      ac.abort();
      this.activeUploads.delete(id);
    }
    this.items = this.items.filter((i) => i.id !== id);
    await deleteStored(id);
    this.notify();
    this.processQueue();
  }

  async retry(id: string): Promise<void> {
    const item = this.items.find((i) => i.id === id);
    if (!item || (item.status !== 'failed' && item.status !== 'paused')) return;
    const updated: QueueItem = {
      ...item,
      status: 'waiting',
      retries: 0,
      error: undefined,
      progress: 0,
    };
    this.items = this.items.map((i) => (i.id === id ? updated : i));
    await this.persistItem(updated);
    this.notify();
    if (this.isOnline) this.processQueue();
  }

  async updateItemMeta(id: string, patch: Partial<QueueItemMeta>): Promise<void> {
    const idx = this.items.findIndex((i) => i.id === id);
    if (idx === -1) return;
    const next = { ...this.items[idx], ...patch };
    this.items[idx] = next;
    this.notify();
    try {
      await this.persistItem(next);
    } catch (e) {
      console.error('[UploadManager] updateItemMeta persist failed', e);
    }
  }

  /** Remove all items that are completed or failed. Returns removed ids for cleanup (e.g. thumbnails). */
  async clearFinished(): Promise<string[]> {
    const toRemove = this.items.filter(
      (i) => i.status === 'completed' || i.status === 'failed'
    );
    const ids = toRemove.map((i) => i.id);
    for (const id of ids) {
      await deleteStored(id);
    }
    this.items = this.items.filter(
      (i) => i.status !== 'completed' && i.status !== 'failed'
    );
    this.notify();
    return ids;
  }

  private isVideo(item: QueueItem): boolean {
    return isVideoFile(item.file);
  }

  private getNextToUpload(): QueueItem | undefined {
    const candidate = this.items.find(
      (i) =>
        (i.status === 'waiting' || i.status === 'paused') &&
        !this.activeUploads.has(i.id)
    );
    if (!candidate) return undefined;
    // Process at most one video at a time (trim + upload)
    if (this.isVideo(candidate)) {
      const anotherVideoActive = this.items.some(
        (i) =>
          i.id !== candidate.id &&
          this.isVideo(i) &&
          (i.status === 'processing' || i.status === 'uploading')
      );
      if (anotherVideoActive) return undefined;
    }
    return candidate;
  }

  private async processQueue(): Promise<void> {
    if (!this.isOnline || this.processing) return;
    const running = this.activeUploads.size;
    if (running >= MAX_CONCURRENT) return;

    const next = this.getNextToUpload();
    if (!next) return;

    this.processing = true;
    const item = next;
    const initialStatus = isVideoFile(item.file) ? 'processing' : 'uploading';
    const updated: QueueItem = { ...item, status: initialStatus, progress: 0 };
    this.items = this.items.map((i) => (i.id === item.id ? updated : i));
    this.notify();
    await this.persistItem(updated);

    const controller = new AbortController();
    this.activeUploads.set(item.id, controller);

    try {
      await this.runOneUpload(updated, controller.signal);
    } finally {
      this.activeUploads.delete(item.id);
      this.processing = false;
      this.notify();
      if (this.isOnline) this.processQueue();
    }
  }

  private async runOneUpload(item: QueueItem, signal: AbortSignal): Promise<void> {
    let currentItem = item;

    const update = (patch: Partial<QueueItem>) => {
      const idx = this.items.findIndex((i) => i.id === currentItem.id);
      if (idx === -1) return;
      const next = { ...this.items[idx], ...patch };
      this.items[idx] = next;
      currentItem = next;
      this.notify();
      this.persistItem(next).catch(() => {});
    };

    // Video auto-trim: if duration > 30s, trim to first 30s (no user confirmation)
    if (isVideoFile(currentItem.file)) {
      try {
        const duration = await getVideoDuration(currentItem.file);
        if (duration > VIDEO_TRIM_THRESHOLD_SECONDS) {
          if (currentItem.status !== 'processing') update({ status: 'processing', progress: 0 });
          if (signal.aborted) return;
          const trimmedFile = await trimVideoTo30Seconds(currentItem.file);
          if (signal.aborted) return;
          update({
            file: trimmedFile,
            fileSize: trimmedFile.size,
            fileKey: fileToKey(trimmedFile),
            fileName: trimmedFile.name,
            status: 'uploading',
            progress: 0,
          });
        } else {
          update({ status: 'uploading', progress: 0 });
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : i18n.t('uploadPage.videoProcessingFailed');
        update({ status: 'failed', error: msg });
        return;
      }
    }

    const formData = new FormData();
    formData.append('file', currentItem.file);

    const isFamily =
      currentItem.uploadDestination === 'family-account' && currentItem.targetFamilyMember;
    const url = isFamily ? '/api/images/upload-family' : '/api/images/upload';
    if (isFamily && currentItem.targetFamilyMember) {
      formData.append('familyMemberId', String(currentItem.targetFamilyMember.otherUserId));
    }

    try {
      const response = await api.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: UPLOAD_TIMEOUT_MS,
        signal,
        onUploadProgress: (ev) => {
          if (ev.total && ev.total > 0) {
            const pct = Math.round((ev.loaded / ev.total) * 100);
            update({ progress: pct });
          }
        },
      });

      const data = response?.data ?? response;
      const mediaFields = fieldsFromUploadResponse(data);
      const message =
        (data as { message?: string })?.message ??
        (isFamily
          ? i18n.t('uploadPage.successOne', {
              fileName: currentItem.fileName,
              name: currentItem.targetFamilyMember?.otherUserFirstName ?? '',
            })
          : i18n.t('uploadPage.successDefault', { fileName: currentItem.fileName }));

      update({
        status: 'completed',
        progress: 100,
        response: data,
        successMessage: message,
        imageId: mediaFields.imageId,
        videoId: mediaFields.videoId,
        streamUrl: mediaFields.streamUrl,
        statusPollUrl: mediaFields.statusPollUrl,
        mediaType: mediaFields.mediaType,
        videoStatus: mediaFields.videoStatus,
        error: undefined,
      });
      if (mediaFields.mediaType === 'VIDEO') {
        startVideoProcessingPoll(mediaFields, (status) => {
          this.items = this.items.map((i) =>
            i.id === item.id ? { ...i, videoStatus: status } : i
          );
          this.notify();
        });
      }
      setTimeout(() => this.notify(), 0);
    } catch (err: unknown) {
      const isAborted =
        err instanceof Error && err.name === 'AbortError';
      if (isAborted) {
        update({ status: 'paused', progress: this.items.find((i) => i.id === item.id)?.progress ?? 0 });
        return;
      }

      const errorMessage =
        (err as { response?: { data?: { message?: string }; status?: number }; message?: string }).response?.data?.message ??
        (err as Error).message ??
        i18n.t('uploadPage.uploadFailed');
      const newRetries = currentItem.retries + 1;

      if (newRetries >= MAX_RETRIES) {
        update({
          status: 'failed',
          error: errorMessage,
          retries: newRetries,
        });
        return;
      }

      update({
        status: 'waiting',
        error: errorMessage,
        retries: newRetries,
        progress: 0,
      });
      const delay = Math.min(1000 * Math.pow(2, newRetries), 30000);
      await new Promise((r) => setTimeout(r, delay));
      if (this.isOnline && !signal.aborted) this.processQueue();
    }
  }
}

const uploadManager = new UploadManager();

// ---------------------------------------------------------------------------
// Album type (unchanged)
// ---------------------------------------------------------------------------

interface Album {
  id: number;
  name: string;
  description?: string;
  imageCount?: number;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// UploadPage component
// ---------------------------------------------------------------------------

const UploadPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [queueState, setQueueState] = useState(uploadManager.getState());
  const [familyMembers, setFamilyMembers] = useState<
    Array<{
      id: number;
      otherUserId: number;
      otherUserFirstName: string;
      otherUserLastName?: string;
      relationshipType?: string;
    }>
  >([]);
  const [showUploadOptions, setShowUploadOptions] = useState(false);
  const [selectedFileForOptions, setSelectedFileForOptions] = useState<QueueItem | null>(null);
  const [selectedAlbumId, setSelectedAlbumId] = useState<number | null>(null);
  const [uploadedImageIds, setUploadedImageIds] = useState<(number | string)[]>([]);
  const [showCreateAlbumModal, setShowCreateAlbumModal] = useState(false);
  const [newAlbumName, setNewAlbumName] = useState('');
  const [newAlbumDescription, setNewAlbumDescription] = useState('');
  const [newAlbumPrice, setNewAlbumPrice] = useState('');
  const [perPhotoPrice, setPerPhotoPrice] = useState('');
  const [newAlbumIsPublic, setNewAlbumIsPublic] = useState(false);
  const [isCreatingAlbum, setIsCreatingAlbum] = useState(false);
  const [isAddingFiles, setIsAddingFiles] = useState(false);
  const thumbnailUrlsRef = useRef<Map<string, string>>(new Map());
  const queueListRef = useRef<HTMLDivElement>(null);
  /** Track imageIds we've already added to each album (so we don't double-call the API in this session) */
  const addedToAlbumRef = useRef<Map<number, Set<number>>>(new Map());
  /** Skip auto-add side effect for items that were already completed before this page mounted */
  const firstAutoAddRunRef = useRef(true);

  const { data: userProfile, isLoading: userLoading, error: userError } = useQuery({
    queryKey: ['userProfile'],
    queryFn: async () => {
      const res = await api.get('/api/auth/profile');
      return res.data;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const { data: storageUsage } = useQuery({
    queryKey: ['storageUsage'],
    queryFn: () => imageService.getStorageUsage(),
    enabled: !!user,
    staleTime: 1 * 60 * 1000,
  });

  const { data: albumsData, refetch: refetchAlbums } = useQuery({
    queryKey: ['albums'],
    queryFn: async () => {
      const res = await api.get('/api/albums');
      return res.data as Album[] | { albums: Album[] };
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const albums = useMemo(() => {
    if (!albumsData) return [];
    if (Array.isArray(albumsData)) return albumsData;
    if (albumsData && typeof albumsData === 'object' && 'albums' in albumsData) return (albumsData as { albums: Album[] }).albums;
    return [];
  }, [albumsData]);

  useEffect(() => {
    uploadManager.loadFromPersisted();
  }, []);

  useEffect(() => {
    const unsub = uploadManager.subscribe(() => {
      setQueueState(() => uploadManager.getState());
    });
    return unsub;
  }, []);

  useEffect(() => {
    const onOnline = () => uploadManager.setOnline(true);
    const onOffline = () => uploadManager.setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    return () => {
      thumbnailUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      thumbnailUrlsRef.current.clear();
    };
  }, []);

  // Scroll queue into view when files are added so user sees selected items
  const prevQueueLengthRef = useRef(0);
  useEffect(() => {
    if (queueState.items.length > 0 && queueState.items.length > prevQueueLengthRef.current) {
      queueListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevQueueLengthRef.current = queueState.items.length;
  }, [queueState.items.length]);

  useEffect(() => {
    fetchFamilyMembers();
  }, []);

  const fetchFamilyMembers = async () => {
    try {
      const response = await api.get('/api/simple-invitations/family-relationships');
      const allClients: Array<{
        id: number;
        otherUserId: number;
        otherUserFirstName: string;
        otherUserLastName?: string;
        relationshipType?: string;
      }> = [];
      const flattenClients = (clients: unknown[]) => {
        if (!Array.isArray(clients)) return;
        clients.forEach((client: unknown) => {
          const c = client as { relation?: string; userId?: number; name?: string; clients?: unknown[] };
          if (c?.relation === 'Client' && c.userId != null) {
            allClients.push({
              id: c.userId,
              otherUserId: c.userId,
              otherUserFirstName: c.name?.split(' ')[0] || (c.name as string) || '',
              otherUserLastName: (c.name as string)?.split(' ').slice(1).join(' ') || '',
              relationshipType: c.relation || 'Client',
            });
          }
          if (c?.clients && Array.isArray(c.clients)) flattenClients(c.clients);
        });
      };
      const data = response?.data;
      if (data?.familyData?.clients) flattenClients(data.familyData.clients);
      else if (Array.isArray(data?.clients)) flattenClients(data.clients);
      else if (Array.isArray(data)) flattenClients(data);
      else if (Array.isArray(data?.data)) flattenClients(data.data);
      const unique = allClients.filter((c, i, a) => a.findIndex((x) => x.id === c.id) === i);
      setFamilyMembers(unique);
    } catch (e) {
      console.error(e);
      setFamilyMembers([]);
    }
  };

  const canUpload = useCallback(() => {
    return !!(userProfile?.canUploadImages && userProfile?.allowedFileTypes);
  }, [userProfile]);

  const VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v'];

  const normalizeAllowedTypes = useCallback((allowedFileTypes: string) => {
    return allowedFileTypes
      .split(',')
      .map((t: string) => t.trim().toLowerCase().replace(/^\./, ''));
  }, []);

  const isFileTypeAllowed = useCallback(
    (file: File) => {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      const isVideoByExt = VIDEO_EXTENSIONS.includes(ext);
      const isVideoByType = file.type.startsWith('video/');
      // Always allow MOV and all video formats when user has upload permission (no profile restriction for video)
      if (userProfile?.canUploadImages && (isVideoByExt || isVideoByType)) {
        return true;
      }
      if (!userProfile?.allowedFileTypes) return false;
      const allowed = normalizeAllowedTypes(userProfile.allowedFileTypes);
      if (allowed.includes(ext)) return true;
      if (isVideoByExt && allowed.some((t: string) => VIDEO_EXTENSIONS.includes(t))) {
        return true;
      }
      return false;
    },
    [userProfile, normalizeAllowedTypes]
  );

  const getAcceptTypes = useCallback(() => {
    if (!userProfile?.canUploadImages) return {};
    const accept: Record<string, string[]> = {};
    // Always allow video (including MOV) when user can upload
    accept['video/*'] = ['.mp4', '.mov', '.avi', '.mkv', '.webm', '.m4v'];
    if (!userProfile?.allowedFileTypes) return accept;
    const allowed = normalizeAllowedTypes(userProfile.allowedFileTypes);
    if (allowed.some((t: string) => ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'heic'].includes(t))) {
      accept['image/*'] = ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp', '.heic'];
    }
    if (allowed.includes('pdf')) accept['application/pdf'] = ['.pdf'];
    if (allowed.some((t: string) => ['doc', 'docx'].includes(t))) {
      accept['application/msword'] = ['.doc'];
      accept['application/vnd.openxmlformats-officedocument.wordprocessingml.document'] = ['.docx'];
    }
    return accept;
  }, [userProfile, normalizeAllowedTypes]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!canUpload()) {
        toast.error(i18n.t('uploadPage.toastNoPermission'));
        return;
      }
      const invalid: string[] = [];
      const valid: File[] = [];
      acceptedFiles.forEach((file) => {
        if (!isFileTypeAllowed(file))
          invalid.push(i18n.t('uploadPage.fileTypeNotAllowed', { name: file.name }));
        else valid.push(file);
      });
      if (invalid.length)
        toast.error(i18n.t('uploadPage.someFilesRejected', { list: invalid.join('\n') }));
      if (valid.length) {
        setIsAddingFiles(true);
        try {
          const { added, skipped, skippedDueToLimit } = await uploadManager.addFiles(valid, {
            targetAlbumId: selectedAlbumId ?? undefined,
          });
          setQueueState(uploadManager.getState());
          if (added) toast.success(i18n.t('uploadPage.filesAddedQueue', { n: added }));
          if (skipped) toast(i18n.t('uploadPage.skippedDuplicates', { n: skipped }));
          if (skippedDueToLimit)
            toast.error(
              i18n.t('uploadPage.queueLimit', { max: MAX_UPLOAD_QUEUE, n: skippedDueToLimit })
            );
        } finally {
          setIsAddingFiles(false);
        }
      }
    },
    [canUpload, isFileTypeAllowed, selectedAlbumId]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: getAcceptTypes(),
    multiple: true,
    disabled: !canUpload() || isAddingFiles,
  });

  const removeFromQueue = useCallback(async (id: string) => {
    const url = thumbnailUrlsRef.current.get(id);
    if (url) {
      URL.revokeObjectURL(url);
      thumbnailUrlsRef.current.delete(id);
    }
    await uploadManager.remove(id);
    setQueueState(uploadManager.getState());
  }, []);

  const retryUpload = useCallback(async (id: string) => {
    await uploadManager.retry(id);
    setQueueState(uploadManager.getState());
  }, []);

  const getThumbnailUrl = (item: QueueItem): string | null => {
    if (thumbnailUrlsRef.current.has(item.id)) return thumbnailUrlsRef.current.get(item.id)!;
    const isImage = item.file.type.startsWith('image/');
    const isVideo = item.file.type.startsWith('video/');
    if (!isImage && !isVideo) return null;
    const url = URL.createObjectURL(item.file);
    thumbnailUrlsRef.current.set(item.id, url);
    return url;
  };

  const isVideoItem = (item: QueueItem): boolean => item.file.type.startsWith('video/');

  const setUploadDestination = useCallback(
    (fileId: string, destination: 'my-account' | 'family-account', familyMember?: (typeof familyMembers)[0]) => {
      const meta: Partial<QueueItemMeta> = {
        uploadDestination: destination,
        targetFamilyMember: familyMember
          ? {
              otherUserId: familyMember.otherUserId,
              otherUserFirstName: familyMember.otherUserFirstName,
              otherUserLastName: familyMember.otherUserLastName,
              relationshipType: familyMember.relationshipType,
            }
          : undefined,
      };
      uploadManager.updateItemMeta(fileId, meta).then(() => {
        if (selectedFileForOptions?.id === fileId) {
          setSelectedFileForOptions(uploadManager.getState().items.find((i) => i.id === fileId) ?? null);
        }
        if (destination === 'my-account' || familyMember) {
          setShowUploadOptions(false);
          setSelectedFileForOptions(null);
        }
      });
    },
    [familyMembers, selectedFileForOptions?.id]
  );

  useEffect(() => {
    // Sync selected file when queue updates
    if (selectedFileForOptions) {
      const current = queueState.items.find((i) => i.id === selectedFileForOptions.id);
      if (current) setSelectedFileForOptions(current);
    }
  }, [queueState.items]);

  const getUploadDestinationText = (item: QueueItem) => {
    if (item.uploadDestination === 'family-account' && item.targetFamilyMember) {
      return `👥 ${t('uploadPage.destOneAccount', { name: item.targetFamilyMember.otherUserFirstName })}`;
    }
    if (item.uploadDestination === 'family-account')
      return `👥 ${t('uploadPage.familyAccountSelectMember')}`;
    return `🏠 ${t('uploadPage.destMyAccount')}`;
  };

  const openUploadOptions = (item: QueueItem) => {
    setSelectedFileForOptions(item);
    setShowUploadOptions(true);
  };

  const handleCreateAlbum = async () => {
    if (!newAlbumName.trim()) {
      toast.error(t('uploadPage.toastAlbumName'));
      return;
    }
    setIsCreatingAlbum(true);
    try {
      const albumData: Record<string, unknown> = {
        name: newAlbumName.trim(),
        isPublic: newAlbumIsPublic,
      };
      if (newAlbumDescription.trim()) albumData.description = newAlbumDescription.trim();
      const price = parseFloat(newAlbumPrice.trim());
      if (!isNaN(price) && price > 0) albumData.perAlbumPrice = price;
      const perPhoto = parseFloat(perPhotoPrice.trim());
      if (!isNaN(perPhoto) && perPhoto > 0) albumData.perPhotoPrice = perPhoto;
      const res = await api.post('/api/albums', albumData);
      toast.success(t('uploadPage.toastAlbumCreated'));
      setShowCreateAlbumModal(false);
      setNewAlbumName('');
      setNewAlbumDescription('');
      setNewAlbumPrice('');
      setPerPhotoPrice('');
      setNewAlbumIsPublic(false);
      handleClearFinished();
      await refetchAlbums();
      if (res.data?.id) setSelectedAlbumId(res.data.id);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('uploadPage.toastAlbumCreateFail');
      toast.error(message);
    } finally {
      setIsCreatingAlbum(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return `0 ${t('uploadPage.sizeBytes')}`;
    const k = 1024;
    const sizes = [
      t('uploadPage.sizeBytes'),
      t('uploadPage.sizeKB'),
      t('uploadPage.sizeMB'),
      t('uploadPage.sizeGB'),
    ];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatRelativeTime = (timestamp: number) => {
    const sec = Math.floor((Date.now() - timestamp) / 1000);
    if (sec < 60) return t('uploadPage.timeJustNow');
    if (sec < 3600) return t('uploadPage.timeMinAgo', { n: Math.floor(sec / 60) });
    if (sec < 86400) return t('uploadPage.timeHrAgo', { n: Math.floor(sec / 3600) });
    return t('uploadPage.timeDayAgo', { n: Math.floor(sec / 86400) });
  };

  const getStatusIcon = (status: QueueItemStatus) => {
    switch (status) {
      case 'completed':
        return <FaCheck className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <FaExclamationTriangle className="h-4 w-4 text-red-500" />;
      case 'paused':
        return <FaClock className="h-4 w-4 text-amber-500" />;
      case 'uploading':
        return <LoadingSpinner size="sm" text="" />;
      case 'processing':
        return <LoadingSpinner size="sm" text="" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status: QueueItemStatus): string => {
    switch (status) {
      case 'completed':
        return t('uploadPage.statusCompleted');
      case 'failed':
        return t('uploadPage.statusFailed');
      case 'paused':
        return t('uploadPage.statusPaused');
      case 'uploading':
        return t('uploadPage.statusUploading');
      case 'processing':
        return t('uploadPage.statusProcessing');
      default:
        return t('uploadPage.statusWaiting');
    }
  };

  const getStatusColor = (status: QueueItemStatus) => {
    switch (status) {
      case 'completed':
        return 'border-green-200 bg-green-50';
      case 'failed':
        return 'border-red-200 bg-red-50';
      case 'paused':
        return 'border-amber-200 bg-amber-50';
      case 'uploading':
        return 'border-blue-200 bg-blue-50';
      case 'processing':
        return 'border-indigo-200 bg-indigo-50';
      default:
        return 'border-gray-200 bg-white';
    }
  };

  const pendingCount = queueState.items.filter((i) => i.status === 'waiting' || i.status === 'paused').length;
  const processingCount = queueState.items.filter((i) => i.status === 'processing').length;
  const finishedCount = queueState.items.filter(
    (i) => i.status === 'completed' || i.status === 'failed'
  ).length;
  const completedWithIds = queueState.items.filter((i) => i.status === 'completed' && i.imageId != null);

  const handleClearFinished = useCallback(async () => {
    const removedIds = await uploadManager.clearFinished();
    removedIds.forEach((id) => {
      const url = thumbnailUrlsRef.current.get(id);
      if (url) {
        URL.revokeObjectURL(url);
        thumbnailUrlsRef.current.delete(id);
      }
    });
    setQueueState(uploadManager.getState());
    if (removedIds.length > 0) {
      toast.success(
        removedIds.length === 1
          ? i18n.t('uploadPage.toastRemovedOne')
          : i18n.t('uploadPage.toastRemovedMany', { n: removedIds.length })
      );
    }
  }, []);

  const addCompletedToAlbum = useCallback(async () => {
    if (!selectedAlbumId || completedWithIds.length === 0) return;
    handleClearFinished();
    const ids = completedWithIds.map((i) => Number(i.imageId!));
    try {
      await api.post(`/api/albums/${selectedAlbumId}/images`, { imageIds: ids });
      // Images are now linked to the selected album; we intentionally skip a toast here
      // to avoid duplicate "image(s) added to album" popups. The upload queue and UI
      // already reflect the updated state.
      setUploadedImageIds((prev) => [...prev, ...ids]);
      if (!addedToAlbumRef.current.has(selectedAlbumId)) addedToAlbumRef.current.set(selectedAlbumId, new Set());
      ids.forEach((id) => addedToAlbumRef.current.get(selectedAlbumId)!.add(id));
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('uploadPage.toastAddAlbumFail');
      toast.error(msg);
    }
  }, [selectedAlbumId, completedWithIds, t]);

  // When runOneUpload completes an item that has targetAlbumId, add that image to that album (e.g. folder-selected or album-selected when files were added)
  const completedWithTargetAlbum = queueState.items.filter(
    (i) => i.status === 'completed' && i.imageId != null && i.targetAlbumId != null
  );
  const completedImageIdsKey = completedWithTargetAlbum.map((i) => `${i.targetAlbumId}:${i.imageId}`).join(',');

  useEffect(() => {
    if (completedWithTargetAlbum.length === 0) return;
    const byAlbum = new Map<number, number[]>();
    for (const item of completedWithTargetAlbum) {
      const albumId = item.targetAlbumId!;
      const id = Number(item.imageId!);
      if (Number.isNaN(id)) continue;
      if (!byAlbum.has(albumId)) byAlbum.set(albumId, []);
      byAlbum.get(albumId)!.push(id);
    }

    // On first run after mount, just seed "already added" state so we don't
    // re-add old completed uploads or show duplicate toasts when opening the page.
    if (firstAutoAddRunRef.current) {
      firstAutoAddRunRef.current = false;
      byAlbum.forEach((imageIds, albumId) => {
        if (!addedToAlbumRef.current.has(albumId)) {
          addedToAlbumRef.current.set(albumId, new Set());
        }
        const setForAlbum = addedToAlbumRef.current.get(albumId)!;
        imageIds.forEach((id) => setForAlbum.add(id));
      });
      return;
    }

    byAlbum.forEach((imageIds, albumId) => {
      if (!addedToAlbumRef.current.has(albumId)) addedToAlbumRef.current.set(albumId, new Set());
      const alreadyAdded = addedToAlbumRef.current.get(albumId)!;
      const toAdd = imageIds.filter((id) => !alreadyAdded.has(id));
      if (toAdd.length === 0) return;
      api
        .post(`/api/albums/${albumId}/images`, { imageIds: toAdd })
        .then(() => {
          toAdd.forEach((id) => alreadyAdded.add(id));
          setUploadedImageIds((prev) => [...prev, ...toAdd]);
          // Intentionally NO toast here to avoid duplicate popups when visiting the page.
          // User will only see a toast when they manually call "Add completed image(s) to this album".
        })
        .catch((err: unknown) => {
          const msg =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            i18n.t('uploadPage.toastAddAlbumFail');
          toast.error(msg);
        });
    });
  }, [completedImageIdsKey, completedWithTargetAlbum, albums]);

  if (userLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <LoadingSpinner size="lg" text={t('uploadPage.loadingProfile')} />
        </div>
      </div>
    );
  }

  if (userError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('uploadPage.profileErrorTitle')}</h1>
          <p className="text-gray-600 mb-4">{t('uploadPage.profileErrorBody')}</p>
          <button onClick={() => window.location.reload()} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700">
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!canUpload()) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-auto">
          <div className="text-red-500 text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('uploadPage.uploadNotAvailable')}</h1>
          <p className="text-gray-600 mb-4">{t('uploadPage.uploadNotAllowedBody')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="text-5xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-6">
          {t('uploadPage.title')}
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          {t('uploadPage.subtitle')}
        </p>
        {userProfile && (
          <div className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 max-w-4xl mx-auto border border-blue-100">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <span className="text-white font-bold text-lg">{userProfile.accountType?.charAt(0) || 'U'}</span>
                </div>
                <h3 className="font-semibold text-gray-800">{t('uploadPage.accountType')}</h3>
                <p className="text-sm text-gray-600">{userProfile.accountType || t('uploadPage.unknown')}</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <span className="text-white font-bold text-xl">∞</span>
                </div>
                <h3 className="font-semibold text-gray-800">{t('uploadPage.fileSize')}</h3>
                <p className="text-sm text-gray-600">{t('uploadPage.unlimited')}</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <span className="text-white font-bold text-lg">{userProfile.allowedFileTypes?.split(',').length || 0}</span>
                </div>
                <h3 className="font-semibold text-gray-800">{t('uploadPage.allowedTypes')}</h3>
                <p className="text-sm text-gray-600">
                  {userProfile.allowedFileTypes?.toUpperCase() || t('uploadPage.none')}
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <span className="text-white font-bold text-sm">
                    {queueState.isOnline ? t('uploadPage.online') : t('uploadPage.offline')}
                  </span>
                </div>
                <h3 className="font-semibold text-gray-800">{t('uploadPage.network')}</h3>
                <p className="text-sm text-gray-600">
                  {queueState.isOnline ? t('uploadPage.uploadsActive') : t('uploadPage.pausedOffline')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Album selection */}
      <div className={`max-w-full mx-auto rounded-2xl p-6 border border-blue-100 transition-opacity ${canUpload() ? 'bg-gradient-to-r from-blue-50 to-purple-50' : 'bg-gray-100 opacity-75 pointer-events-none'}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <FaFolder className="mr-3 font-medium text-[#2731db]" />
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{t('uploadPage.selectAlbumTitle')}</h3>
              <p className="text-sm text-gray-600">{t('uploadPage.selectAlbumHint')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={() => setShowCreateAlbumModal(true)}
              data-ai-action="create-event"
              disabled={!canUpload()}
              className="flex items-center px-4 py-2 rounded-lg bg-[#2731db] text-white hover:bg-blue-700 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaPlus className="mr-2" />
              {t('uploadPage.createAlbum')}
            </button>
            {albums.length > 0 && (
              <select
                value={selectedAlbumId ?? ''}
                onChange={(e) => setSelectedAlbumId(e.target.value ? Number(e.target.value) : null)}
                disabled={!canUpload()}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#2731db] min-w-[200px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">{t('uploadPage.noAlbum')}</option>
                {albums.map((album) => (
                  <option key={album.id} value={album.id}>
                    {album.name}{' '}
                    {album.imageCount != null ? t('uploadPage.imagesCount', { n: album.imageCount }) : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
        {selectedAlbumId && (
          <div className="mt-3 p-3 bg-blue-100 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              ✓ {t('uploadPage.imagesAddedTo')} <strong>{albums.find((a) => a.id === selectedAlbumId)?.name}</strong>
            </p>
            {completedWithIds.length > 0 && (
              <button
                onClick={addCompletedToAlbum}
                className="mt-2 text-sm text-blue-700 underline hover:no-underline"
              >
                {t('uploadPage.addCompletedHint', { n: completedWithIds.length })}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Dropzone */}
      <div className="bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 rounded-3xl shadow-2xl border border-blue-100/50 relative">
        {isAddingFiles && (
          <div className="absolute inset-0 rounded-3xl bg-indigo-500/10 backdrop-blur-sm z-10 flex items-center justify-center">
            <div className="bg-white rounded-2xl shadow-xl px-8 py-6 flex items-center gap-4">
              <LoadingSpinner size="md" text="" />
              <div>
                <p className="font-semibold text-gray-800">{t('uploadPage.addingFiles')}</p>
                <p className="text-sm text-gray-600">{t('uploadPage.addingFilesWait', { max: MAX_UPLOAD_QUEUE })}</p>
              </div>
            </div>
          </div>
        )}
        <div className="p-10">
          <div
            {...getRootProps()}
            data-ai-action="upload-family-images"
            className={`border-3 border-dashed rounded-3xl p-16 text-center cursor-pointer transition-all duration-300 ${
              isDragActive ? 'border-indigo-400 bg-gradient-to-br from-indigo-50 to-purple-50' : 'border-gray-300 hover:border-indigo-400 hover:bg-gradient-to-br from-blue-50/50 to-purple-50/50'
            } ${isAddingFiles ? 'pointer-events-none opacity-70' : ''}`}
          >
            <input {...getInputProps()} data-ai-upload="upload_family_images" />
            <FaCloudUploadAlt className="mx-auto h-20 w-20 text-indigo-500 mb-6" />
            <p className="mt-6 text-2xl font-bold text-gray-800">
              {isDragActive ? t('uploadPage.dropFilesHere') : t('uploadPage.dragDropHere')}
            </p>
            <p className="mt-3 text-lg text-gray-600">{t('uploadPage.clickToSelect')}</p>
            <p className="mt-2 text-sm text-gray-500">
              {t('uploadPage.videoTrimHint', { max: MAX_UPLOAD_QUEUE })}
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-4 text-sm">
              {userProfile?.allowedFileTypes && (
                <span className="flex items-center bg-white/70 px-4 py-2 rounded-full shadow-sm">
                  <span className="w-3 h-3 bg-green-400 rounded-full mr-3 animate-pulse" />
                  <span className="font-medium text-gray-700">{userProfile.allowedFileTypes.toUpperCase()}</span>
                </span>
              )}
              <span className="flex items-center bg-white/70 px-4 py-2 rounded-full shadow-sm">
                <span className="w-3 h-3 bg-blue-400 rounded-full mr-3 animate-pulse" />
                <span className="font-medium text-gray-700">{t('uploadPage.allowedTypesBadge')}</span>
              </span>
              {storageUsage && (
                <span className="flex items-center bg-white/70 px-4 py-2 rounded-full shadow-sm">
                  <span className="w-3 h-3 bg-purple-400 rounded-full mr-3 animate-pulse" />
                  <span className="font-medium text-gray-700">
                    {t('uploadPage.mbAvailable', { n: storageUsage.total - storageUsage.used })}
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Queue list - scroll into view when files added */}
      {queueState.items.length > 0 && (
        <div ref={queueListRef} className="bg-gradient-to-br from-white via-blue-50/20 to-purple-50/20 rounded-3xl shadow-2xl border border-blue-100/50">
          <div className="px-6 sm:px-10 py-6 sm:py-8 border-b border-blue-200/50 bg-gradient-to-r from-indigo-50/50 to-purple-50/50 rounded-t-3xl">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-4 min-w-0">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <FaFileImage className="h-6 w-6 text-white" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">{t('uploadPage.uploadQueue')}</h2>
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mt-1">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg bg-indigo-100 text-indigo-800 font-semibold tabular-nums text-base shrink-0">
                      {t('uploadPage.filesOfMax', { current: queueState.items.length, max: MAX_UPLOAD_QUEUE })}
                    </span>
                    {queueState.items.length >= MAX_UPLOAD_QUEUE && (
                      <span className="text-amber-600 text-sm font-medium shrink-0">{t('uploadPage.maxLimit')}</span>
                    )}
                    <span className="text-gray-500 text-sm">·</span>
                    <span className="text-gray-600 text-sm tabular-nums">
                      {t('uploadPage.waiting', { n: pendingCount })}
                    </span>
                    {processingCount > 0 && (
                      <>
                        <span className="text-gray-400">·</span>
                        <span className="text-indigo-600 text-sm tabular-nums">
                          {t('uploadPage.processingCount', { n: processingCount })}
                        </span>
                      </>
                    )}
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-600 text-sm">
                      {queueState.isOnline ? t('uploadPage.online') : t('uploadPage.paused')}
                    </span>
                  </div>
                </div>
              </div>
              {finishedCount > 0 && (
                <button
                  type="button"
                  onClick={handleClearFinished}
                  className="px-4 py-2 rounded-xl text-sm font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors shrink-0"
                >
                  {t('uploadPage.clearFinished', { n: finishedCount })}
                </button>
              )}
            </div>
          </div>
          {isAddingFiles && (
            <div className="px-6 py-3 bg-indigo-100 border-b border-indigo-200 flex items-center justify-center gap-2 text-sm text-indigo-800 font-medium">
              <LoadingSpinner size="sm" text="" />
              <span>{t('uploadPage.addingMoreFiles', { max: MAX_UPLOAD_QUEUE })}</span>
            </div>
          )}
          <div className="relative flex flex-col min-h-[320px] max-h-[70vh] h-[70vh]">
            <div className="sticky top-0 z-10 px-4 py-2 bg-indigo-50/95 border-b border-indigo-100/80 backdrop-blur-sm flex items-center justify-center gap-2 text-sm text-gray-700 shrink-0">
              <span className="tabular-nums font-semibold text-indigo-800">{queueState.items.length}</span>
              <span>{t('uploadPage.filesInQueueLine')}</span>
              <span className="text-gray-400">{t('uploadPage.scrollToSeeAll')}</span>
            </div>
            <div className="p-6 md:p-8 overflow-y-auto overflow-x-hidden scroll-smooth flex-1 min-h-0 basis-0">
            <div className="grid grid-cols-1 gap-4">
              {queueState.items.map((item) => {
                const isVideo = isVideoItem(item);
                const isUploading = item.status === 'uploading';
                const progressBarColor =
                  item.status === 'completed'
                    ? 'bg-green-500'
                    : item.status === 'uploading' || item.status === 'processing'
                      ? 'bg-blue-500'
                      : 'bg-gray-300';
                return (
                  <div
                    key={item.id}
                    className={`flex flex-col md:flex-row md:items-center gap-4 p-4 rounded-[14px] border shadow-sm hover:shadow-md transition-shadow duration-200 ${getStatusColor(item.status)}`}
                  >
                    {/* Left: Icon only (no image preview) */}
                    <div className="w-full h-[90px] md:w-[120px] md:h-[90px] md:flex-shrink-0 rounded-lg bg-gray-100 flex items-center justify-center">
                      {isVideo ? (
                        <FaVideo className="h-10 w-10 text-indigo-500" />
                      ) : (
                        <FaFileImage className="h-10 w-10 text-gray-500" />
                      )}
                    </div>

                    {/* Center: Details + progress */}
                    <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                      <p className="font-bold text-gray-900 truncate" title={item.fileName}>
                        {item.fileName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatRelativeTime(item.createdAt)} · {formatFileSize(item.fileSize)}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5 h-10">
                        {item.status === 'processing' && (
                          <>
                            {getStatusIcon(item.status)}
                            <span className="text-sm font-medium text-indigo-600">{t('uploadPage.processingVideo')}</span>
                          </>
                        )}
                        {item.status === 'uploading' && (
                          <>
                            {getStatusIcon(item.status)}
                            <span className="text-sm font-medium text-blue-600">{t('uploadPage.uploading')}</span>
                          </>
                        )}
                        {(item.status === 'waiting' || item.status === 'paused') && (
                          <span className="text-sm font-medium text-gray-600">{getStatusLabel(item.status)}</span>
                        )}
                        {item.status === 'completed' && (
                          <span className="text-sm font-medium text-green-700 flex items-center gap-1.5">
                            {getStatusIcon(item.status)}
                            {t('uploadPage.completed')}
                          </span>
                        )}
                        {item.status === 'failed' && (
                          <span className="text-sm font-medium text-red-700 flex items-center gap-1.5">
                            {getStatusIcon(item.status)}
                            {t('uploadPage.failed')}
                          </span>
                        )}
                      </div>
                      {/* Progress bar: 6px, rounded, blue when uploading, green when completed */}
                      <div className="mt-1.5 h-1.5 w-full max-w-xs bg-gray-200 rounded-full overflow-hidden">
                        {item.status === 'processing' ? (
                          <div className="h-full w-full rounded-full bg-blue-400 animate-pulse" />
                        ) : (
                          <div
                            className={`h-full rounded-full transition-all duration-500 ease-out ${progressBarColor}`}
                            style={{ width: item.status === 'completed' ? '100%' : `${item.progress}%` }}
                          />
                        )}
                      </div>
                      {(item.status === 'waiting' || item.status === 'paused') && (
                        <button
                          type="button"
                          onClick={() => openUploadOptions(item)}
                          className="mt-1 text-sm font-medium text-purple-600 hover:text-purple-700"
                        >
                          {t('uploadPage.chooseDestination')}
                        </button>
                      )}
                      {item.status === 'failed' && (
                        <button
                          type="button"
                          onClick={() => retryUpload(item.id)}
                          className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-amber-600 hover:text-amber-700"
                        >
                          <FaRedoAlt className="h-4 w-4" /> {t('uploadPage.retry')}
                        </button>
                      )}
                      {item.status === 'paused' && (
                        <p className="text-xs text-amber-700 mt-0.5">{t('uploadPage.pausedOfflineResume')}</p>
                      )}
                      {item.successMessage && item.status === 'completed' && (
                        <p className="text-xs text-green-700 mt-0.5 truncate">{item.successMessage}</p>
                      )}
                      {item.error && (
                        <p className="text-xs text-red-700 mt-0.5 break-words">
                          {item.error}
                          {item.retries > 0 &&
                            ` ${t('uploadPage.retryProgress', { current: item.retries, max: MAX_RETRIES })}`}
                        </p>
                      )}
                    </div>

                    {/* Right: Delete button - circular, centered, disabled during upload */}
                    <div className="flex md:flex-shrink-0 justify-end md:justify-center items-center">
                      <button
                        type="button"
                        onClick={() =>removeFromQueue(item.id)}
                        // onClick={() => !isUploading && removeFromQueue(item.id)}
                        // disabled={isUploading}
                        className="w-10 h-10 rounded-full bg-red-100 hover:bg-red-200 text-red-600 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label={t('uploadPage.removeFromQueue')}
                      >
                        <FaTimes className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      {queueState.items.length > 0 && (
        <div className="bg-gradient-to-br from-white via-blue-50/20 to-purple-50/20 rounded-3xl shadow-2xl border border-blue-100/50 p-10">
          <div className="flex items-center space-x-4 mb-8">
            <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
              <FaCloudUploadAlt className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800">{t('uploadPage.uploadSummary')}</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-blue-100/50">
              <div className="text-center">
                <div className="w-14 h-14 bg-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <span className="text-white font-bold text-lg tabular-nums">{queueState.items.length}</span>
                </div>
                <p className="text-sm font-semibold text-gray-800">{t('uploadPage.total')}</p>
                <p className="text-xs text-gray-500 mt-0.5">{t('uploadPage.maxFilesHint', { max: MAX_UPLOAD_QUEUE })}</p>
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-blue-100/50">
              <div className="text-center">
                <div className="w-14 h-14 bg-indigo-400 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <span className="text-white font-bold text-lg">{processingCount}</span>
                </div>
                <p className="text-sm font-semibold text-gray-800">{t('uploadPage.processing')}</p>
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-blue-100/50">
              <div className="text-center">
                <div className="w-14 h-14 bg-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <span className="text-white font-bold text-lg">
                    {queueState.items.filter((i) => i.status === 'uploading').length}
                  </span>
                </div>
                <p className="text-sm font-semibold text-gray-800">{t('uploadPage.uploading')}</p>
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-blue-100/50">
              <div className="text-center">
                <div className="w-14 h-14 bg-green-500 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <span className="text-white font-bold text-lg">
                    {queueState.items.filter((i) => i.status === 'completed').length}
                  </span>
                </div>
                <p className="text-sm font-semibold text-gray-800">{t('uploadPage.completed')}</p>
              </div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-blue-100/50">
              <div className="text-center">
                <div className="w-14 h-14 bg-red-500 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <span className="text-white font-bold text-lg">
                    {queueState.items.filter((i) => i.status === 'failed').length}
                  </span>
                </div>
                <p className="text-sm font-semibold text-gray-800">{t('uploadPage.failed')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload options modal */}
      {showUploadOptions && selectedFileForOptions && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">{t('uploadPage.modalDestinationTitle')}</h3>
                <button
                  onClick={() => {
                    setShowUploadOptions(false);
                    setSelectedFileForOptions(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">{t('uploadPage.selectedFile')}</h4>
                  <div className="flex items-center space-x-3">
                    <FaFileImage className="h-8 w-8 text-blue-500" />
                    <div>
                      <p className="font-medium text-gray-900">{selectedFileForOptions.fileName}</p>
                      <p className="text-sm text-gray-500">{formatFileSize(selectedFileForOptions.fileSize)}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
                    <div className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="uploadDestination"
                        checked={selectedFileForOptions.uploadDestination === 'my-account'}
                        onChange={() => setUploadDestination(selectedFileForOptions.id, 'my-account')}
                        className="text-blue-600"
                      />
                      <div>
                        <label className="font-medium text-blue-900">🏠 {t('uploadPage.myAccountTitle')}</label>
                        <p className="text-sm text-blue-700">{t('uploadPage.myAccountDesc')}</p>
                      </div>
                    </div>
                  </div>
                  {familyMembers.length > 0 && (
                    <div className="bg-purple-50 rounded-lg p-4 border-2 border-purple-200">
                      <div className="flex items-center space-x-3 mb-3">
                        <input
                          type="radio"
                          name="uploadDestination"
                          checked={selectedFileForOptions.uploadDestination === 'family-account'}
                          onChange={() => setUploadDestination(selectedFileForOptions.id, 'family-account')}
                          className="text-purple-600"
                        />
                        <div>
                          <label className="font-medium text-purple-900">👥 {t('uploadPage.clientAccountTitle')}</label>
                          <p className="text-sm text-purple-700">{t('uploadPage.clientStoreDesc')}</p>
                        </div>
                      </div>
                      {selectedFileForOptions.uploadDestination === 'family-account' && (
                        <div className="ml-6">
                          <select
                            value={selectedFileForOptions.targetFamilyMember?.otherUserId ?? ''}
                            onChange={(e) => {
                              const member = familyMembers.find((m) => m.otherUserId === Number(e.target.value));
                              if (member) setUploadDestination(selectedFileForOptions.id, 'family-account', member);
                            }}
                            className="w-full p-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">{t('uploadPage.selectClient')}</option>
                            {familyMembers.map((member) => (
                              <option key={member.id} value={member.otherUserId}>
                                {member.otherUserFirstName} {member.otherUserLastName} ({member.relationshipType})
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex justify-end pt-4">
                  <button
                    onClick={() => {
                      setShowUploadOptions(false);
                      setSelectedFileForOptions(null);
                    }}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {t('uploadPage.done')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create album modal */}
      {showCreateAlbumModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                <FaFolder className="mr-2 text-[#2731db]" />
                {t('uploadPage.createNewAlbum')}
              </h2>
              <button
                onClick={() => {
                  setShowCreateAlbumModal(false);
                  setNewAlbumName('');
                  setNewAlbumDescription('');
                  setNewAlbumPrice('');
                  setNewAlbumIsPublic(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('uploadPage.albumName')}</label>
                <input
                  type="text"
                  value={newAlbumName}
                  onChange={(e) => setNewAlbumName(e.target.value)}
                  placeholder={t('uploadPage.albumNamePlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2731db]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('uploadPage.albumDesc')}</label>
                <textarea
                  value={newAlbumDescription}
                  onChange={(e) => setNewAlbumDescription(e.target.value)}
                  placeholder={t('uploadPage.albumDescPlaceholder')}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2731db]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('uploadPage.albumPrice')}</label>
                <input
                  type="number"
                  value={newAlbumPrice}
                  onChange={(e) => setNewAlbumPrice(e.target.value)}
                  placeholder={t('uploadPage.albumPricePlaceholder')}
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2731db]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('uploadPage.perPhotoPrice')}</label>
                <input
                  type="number"
                  value={perPhotoPrice}
                  onChange={(e) => setPerPhotoPrice(e.target.value)}
                  placeholder={t('uploadPage.perPhotoPlaceholder')}
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2731db]"
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isPublic"
                  checked={newAlbumIsPublic}
                  onChange={(e) => setNewAlbumIsPublic(e.target.checked)}
                  className="w-4 h-4 text-[#2731db] border-gray-300 rounded"
                />
                <label htmlFor="isPublic" className="text-sm font-medium text-gray-700">
                  {t('uploadPage.makeAlbumPublic')}
                </label>
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleCreateAlbum}
                  disabled={isCreatingAlbum || !newAlbumName.trim()}
                  className="flex-1 px-4 py-2 rounded-lg bg-[#2731db] text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreatingAlbum ? t('uploadPage.creating') : t('uploadPage.createAlbumBtn')}
                </button>
                <button
                  onClick={() => {
                    setShowCreateAlbumModal(false);
                    setNewAlbumName('');
                    setNewAlbumDescription('');
                  }}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadPage;
